import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {MigrationError} from 'config-migration/MigrationConstants';
import {MigrationTab} from 'config-migration/types/MigrationTab';
import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import {ORANGE_DARK, RED_DARK} from 'ui-library/Colors';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface InlineIconProps {
  content: JSX.Element;
  iconClassName: string;
  icon: BenchlingIconName;
}

function InlineIcon(props: InlineIconProps): JSX.Element {
  return (
    <Popover
      renderContent={() => <div className={css(styles.content)}>{props.content}</div>}
      placement={FloatingPlacement.TOP}
      interaction={FloatingInteraction.CLICK}
    >
      <BenchlingIcon icon={props.icon} className={classNames(props.iconClassName, css(styles.inlineIcon))} />
    </Popover>
  );
}

export interface WrapperInlineIconProps {
  objectName: string;
  errors: Array<MigrationError>;
  /** Name of icon to use instead of the default for the InlineIcon type */
  iconOverride?: BenchlingIconName;
  migrationTab?: MigrationTab;
}

function getErrorMessageSuffix(migrationStage?: MigrationTab): string {
  return (migrationStage ?? MigrationTab.IMPORT) === MigrationTab.IMPORT ? 'imported' : 'exported';
}

// `${logMessagePrefix} can be imported, but will either have references removed or require changes:
export function InlineWarningIcon(props: WrapperInlineIconProps): JSX.Element {
  return (
    <InlineIcon
      content={
        <>
          <p>
            {props.migrationTab === MigrationTab.IMPORT ? (
              <>
                <strong>{props.objectName}</strong> can be imported, but will either have references removed
                or require changes.
              </>
            ) : (
              <>
                <strong>{props.objectName}</strong> was exported, but will not be importable as-is. There will
                either be references replaced or changes required.
              </>
            )}
          </p>
          <ul className={css(styles.list)}>
            {props.errors.map(({message}) => (
              <li key={_.uniqueId('warning-')}>{message}</li>
            ))}
          </ul>
        </>
      }
      icon={props.iconOverride ?? 'warning'}
      iconClassName={css(styles.warningIcon)}
    />
  );
}

export function InlineErrorIcon(props: WrapperInlineIconProps): JSX.Element {
  return (
    <InlineIcon
      content={
        <>
          <p>
            <strong>{props.objectName}</strong> cannot be {getErrorMessageSuffix(props.migrationTab)}.
          </p>
          <ul className={css(styles.list)}>
            {props.errors
              .filter((error) => error.level === LogLevel.ERROR)
              .map(({message}) => (
                <li key={_.uniqueId('error-')}>{message}</li>
              ))}
          </ul>
        </>
      }
      icon={props.iconOverride ?? 'error'}
      iconClassName={css(styles.errorIcon)}
    />
  );
}

const styles = StyleSheet.create({
  content: {
    ...LEGACY_FONT_PROXIMA_13,
  },
  list: {
    paddingInlineStart: 20,
  },
  inlineIcon: {
    cursor: 'pointer',
    flex: 'none',
    marginRight: 6,
  },
  errorIcon: {
    color: RED_DARK,
  },
  warningIcon: {
    color: ORANGE_DARK,
  },
});
